import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { api } from './convex/_generated/api';
import { useAction } from "convex/react";
import { toast } from 'sonner';

export default function Auth2() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const createUser = useAction(api.mfa.createUsers);
  const loginUser = useAction(api.mfa.loginUser);
  const sendOTP = useAction(api.mfa.sendOTP);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const user = await loginUser({ email, password });
        if (!user) {
          toast.error("User not found");
        } else {
          toast.success(`Welcome back, ${user.fullName || user.email}`);
          if (!user.mfaEnabled) {
            navigate("/mfa-setup", { state: { userId: user.userId, email: user.email } });
          } else {
            await sendOTP({ userId: user.userId, email: user.email });
            navigate("/mfa-verify", { state: { userId: user.userId, email: user.email } });
          }
        }
      } else {
        if (!fullName.trim()) {
          toast.error("Please enter your full name");
          return;
        }
        const user = await createUser({ email, password, fullName });
        toast.success("Account created!");
        if (!user.mfaEnabled) {
          navigate("/mfa-setup", { state: { userId: user.userId, email: user.email } });
        } else {
          navigate("/mfa-verify", { state: { userId: user.userId, email: user.email } });
        }
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.glow1} />
      <div style={S.glow2} />

      {/* Header */}
      <header style={S.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>⚡</span>
          <span style={S.logoText}>SayIt</span>
          <span style={S.logoBeta}>BETA</span>
        </div>
      </header>

      <div style={S.center}>
        <div style={S.card}>

          {/* Icon */}
          <div style={S.iconWrap}>
            <Zap size={28} color="#fff" />
          </div>

          <h1 style={S.title}>
            {isLogin ? 'Welcome back' : 'Create account'}
          </h1>
          <p style={S.subtitle}>
            {isLogin
              ? 'Sign in to access your scripts'
              : 'Start saying what needs to be said'}
          </p>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {!isLogin && (
              <div style={S.fieldWrap}>
                <label style={S.label}>Full Name</label>
                <div style={S.inputWrap}>
                  <User size={16} style={S.inputIcon} />
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    style={S.input}
                    required
                  />
                </div>
              </div>
            )}

            <div style={S.fieldWrap}>
              <label style={S.label}>Email</label>
              <div style={S.inputWrap}>
                <Mail size={16} style={S.inputIcon} />
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={S.input}
                  required
                />
              </div>
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Password</label>
              <div style={S.inputWrap}>
                <Lock size={16} style={S.inputIcon} />
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={S.input}
                  required
                  minLength={8}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ ...S.btn, opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
            >
              {loading ? (
                <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              style={S.toggleBtn}
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    width: "1900px",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
    fontFamily: "'DM Sans', sans-serif",
    position: "relative",
    overflow: "hidden",
  },
  glow1: {
    position: "fixed",
    top: -100, right: -100,
    width: 400, height: 400,
    background: "rgba(124, 58, 237, 0.3)",
    borderRadius: "50%",
    filter: "blur(120px)",
    pointerEvents: "none",
  },
  glow2: {
    position: "fixed",
    bottom: -100, left: -100,
    width: 400, height: 400,
    background: "rgba(74, 222, 128, 0.15)",
    borderRadius: "50%",
    filter: "blur(120px)",
    pointerEvents: "none",
  },
  header: {
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    padding: "0 24px",
    height: 60,
    display: "flex",
    alignItems: "center",
    position: "sticky",
    top: 0,
    background: "rgba(15,12,41,0.96)",
    backdropFilter: "blur(16px)",
    zIndex: 100,
  },
  logoText: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 22,
    fontWeight: 700,
    color: "#E8E8EC",
  },
  logoBeta: {
    fontSize: 10,
    fontWeight: 600,
    color: "#C8A96E",
    border: "1px solid #C8A96E",
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: "0.08em",
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 60px)",
    padding: 20,
    position: "relative",
    zIndex: 1,
  },
  card: {
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 24,
    padding: "40px 36px",
    width: "100%",
    maxWidth: 420,
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: "linear-gradient(135deg, #7c3aed, #4ade80)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 700,
    color: "#fff",
    textAlign: "center",
    margin: "0 0 8px",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: 14,
    color: "rgba(255,255,255,0.55)",
    textAlign: "center",
    lineHeight: 1.6,
    margin: "0 0 28px",
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "rgba(255,255,255,0.7)",
  },
  inputWrap: {
    position: "relative",
  },
  inputIcon: {
    position: "absolute",
    top: "50%",
    left: 12,
    transform: "translateY(-50%)",
    color: "rgba(255,255,255,0.3)",
  },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 12,
    padding: "12px 12px 12px 38px",
    color: "#fff",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    boxSizing: "border-box",
  },
  btn: {
    width: "100%",
    background: "linear-gradient(90deg, #7c3aed, #4ade80)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "14px 20px",
    fontSize: 15,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 8,
    transition: "opacity 0.2s",
  },
  toggleBtn: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.4)",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
  },
};